'use client'

import React, { Children, HTMLAttributes, KeyboardEvent, ReactElement, ReactNode, cloneElement, isValidElement, useRef } from 'react'

interface TabListProps extends Omit<HTMLAttributes<HTMLDivElement>, 'role'> {
    children: ReactNode
    ariaLabel?: string
    ariaLabelledBy?: string
}

const focusByOffset = (tabButtons: HTMLElement[], currentIndex: number, offset: number) => {
    const nextIndex = (currentIndex + offset + tabButtons.length) % tabButtons.length
    tabButtons[nextIndex]?.focus()
}

export const TabList = ({ children, ariaLabel, ariaLabelledBy, ...props }: TabListProps) => {
    const tabRefs = useRef<Array<HTMLElement | null>>([])

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const tabButtons = tabRefs.current.filter((element): element is HTMLElement => element !== null)

        if (!tabButtons.length) return

        const currentTarget = event.target as HTMLElement
        const currentIndex = tabButtons.findIndex((tab) => tab === currentTarget)

        if (currentIndex < 0) return

        if (event.key === 'ArrowRight') {
            event.preventDefault()
            focusByOffset(tabButtons, currentIndex, 1)
            return
        }

        if (event.key === 'ArrowLeft') {
            event.preventDefault()
            focusByOffset(tabButtons, currentIndex, -1)
            return
        }

        if (event.key === 'Home') {
            event.preventDefault()
            tabButtons[0]?.focus()
            return
        }

        if (event.key === 'End') {
            event.preventDefault()
            tabButtons[tabButtons.length - 1]?.focus()
            return
        }

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            tabButtons[currentIndex]?.click()
        }
    }

    return (
        <div
            {...props}
            role="tablist"
            tabIndex={-1}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            onKeyDown={onKeyDown}
            className={`tabs flex w-full horizontal ${props.className ?? ''}`}
        >
            {/* We inject a ref into each Tab child so TabList can implement roving focus (Arrow/Home/End) without requiring consumers to wire refs manually. */}
            {Children.map(children, (child, index) => {
                if (!isValidElement(child)) return child

                return cloneElement(child as ReactElement<{ tabRef?: (node: HTMLElement | null) => void }>, {
                    tabRef: (node: HTMLElement | null) => {
                        tabRefs.current[index] = node
                    },
                })
            })}
            <span className="tabs__filler bg-shade-opacity-100 border-b border-shade-opacity-100 grow h-auto" />
        </div>
    )
}
